<script setup lang="ts">
import { Modal } from 'flowbite'
import { onMounted, ref, watch } from 'vue'
import type { InstanceOptions, ModalInterface, ModalOptions } from 'flowbite'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { FreeMode, Keyboard, Navigation, Thumbs } from 'swiper/modules'
import { toQueryUrl } from '@ui/composables'
import 'swiper/css'
import 'swiper/css/free-mode'
import 'swiper/css/thumbs'
import 'swiper/css/navigation'

interface Props {
  id: string
  show: boolean
  loading: boolean
  classes?: string
  dimension?: string
  modelValue: Record<string, any>
  thumbsSwiper: Record<string, any>
  labels?: Record<string, any>
  profile?: Record<string, any>
  galleries: any[]
}

const props = withDefaults(defineProps<Props>(), {
  id: 'modal-gallery',
  show: false,
  loading: false,
  classes: 'bg-white',
  dimension: '1600x1066',
  modelValue: () => ({}),
  thumbsSwiper: () => ({}),
  labels: () => ({}),
  profile: () => ({}),
  galleries: () => [],
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: Record<string, any>): void
  (e: 'update:show', value: boolean): void
  (e: 'update:thumbsSwiper', value: Record<string, any>): void
}>()

const modal = ref<ModalInterface>({})

const mutable = ref({
  modelValue: {} as Record<string, any>,
  thumbsSwiper: {} as Record<string, any>,
  galleries: [] as any[],
  swipers: {} as Record<string, any>,
})

function onSwiper(el: any) {
  if (props.modelValue?.el)
    mutable.value.modelValue.el = el

  mutable.value.swipers = el
  mutable.value.thumbsSwiper = el
}

function handleSwiper(el: any) {
  if (el?.activeIndex !== undefined && props.modelValue?.el)
    mutable.value.modelValue.index = el.activeIndex
}

function setThumbsSwiper(swiper: any) {
  mutable.value.thumbsSwiper = swiper
}

function init(id: string) {
  const targetEl = document.getElementById(id)
  if (!targetEl)
    return

  if (props.galleries.length && props.modelValue?.el) {
    mutable.value.modelValue = props.modelValue
    mutable.value.galleries = props.galleries
    if (props.thumbsSwiper)
      mutable.value.thumbsSwiper = props.thumbsSwiper
  }

  const options: ModalOptions = {
    placement: 'center',
    backdrop: 'dynamic',
    backdropClasses: 'bg-white fixed inset-0 z-40 transition duration-300 delay-150 ease-linear overflow-hidden',
    closable: true,
    onHide: () => {
      emit('update:show', false)
      emit('update:modelValue', {})
    },
    onToggle: () => {},
  }

  const instanceOptions: InstanceOptions = {
    id,
    override: true,
  }
  modal.value = new Modal(targetEl, options, instanceOptions)
}

function slideTo(index: number) {
  if (mutable.value.modelValue?.el && Object.keys(mutable.value.modelValue).length)
    mutable.value.modelValue.el.slideTo(index)
}

watch(() => props.show, (cur) => {
  try {
    if (!modal.value || typeof modal.value !== 'object' || !modal.value.show)
      init(props.id)

    if (cur && modal.value) {
      modal.value.show()
      if (mutable.value.modelValue.el?.slideTo)
        mutable.value.modelValue.el.slideTo(props.modelValue.index)
    }
  }
  catch (e) {
    console.warn('[modal-gallery]', e)
  }
})

onMounted(() => {
  if (props.id && props.galleries.length)
    init(props.id)
  else
    console.warn('[onMounted] error galleries...')
})
</script>

<template>
  <div
    :id="props.id || 'modal-gallery'"
    tabindex="-1"
    aria-hidden="true"
    :class="`modal-gallery fixed left-0 right-0 top-0 z-50 hidden h-[calc(100%-1rem)] max-h-[100vh] w-full overflow-x-hidden overflow-y-hidden pt-4 transition duration-300 delay-150 ease-linear md:inset-0 lg:px-4 lg:pb-4 ${classes}`"
  >
    <div
      v-if="modelValue && typeof modelValue === 'object' && mutable.galleries.length"
      class="modal-body h-full w-full flex flex-col items-center justify-center"
    >
      <div class="modal-header relative mb-5 w-full lg:px-6 max-lg:px-4">
        <div class="w-full flex items-start items-center justify-between">
          <span class="me-2 inline-flex items-center rounded-md bg-primary px-2 py-1 text-base text-white font-medium dark:bg-gray-700 dark:text-gray-300">
            {{ `${mutable.modelValue.index + 1} / ${mutable.galleries.length}` }}
          </span>
          <button
            type="button"
            title="close"
            class="text-gray"
            @click="modal.hide()"
          >
            <IClose class="h-6 w-6" size="24"/>
            <span class="sr-only">Close modal</span>
          </button>
        </div>
      </div>
      <div class="relative h-full w-full flex justify-center overflow-hidden">
        <div
          v-if="mutable.galleries.length > 1"
          :style="{ '--swiper-navigation-color': 'var(--color-primary)' }"
          class="swiper-gallery-navigation absolute left-0 right-0 z-2 mx-auto max-w-screen-lg items-center justify-between max-lg:hidden lg:max-w-screen-xl lg:flex"
        >
          <span class="swiper-button-prev text-2xl" />
          <span class="swiper-button-next text-2xl" />
        </div>
        <div class="flex-lg-grow-1 pb-lg-0 relative flex items-center justify-center pb-2">
          <div class="mx-auto my-auto h-full flex flex-col items-center justify-center lg:max-w-screen-lg max-lg:max-w-screen-sm">
            <div class="relative mx-auto my-auto h-full w-full flex justify-center overflow-hidden text-center lg:max-w-5xl">
              <div class="swiper-dialog">
                <div class="section-slider-wrapper relative mx-auto h-full w-full flex flex-col justify-center overflow-hidden text-center lg:my-auto max-lg:mt-4 lg:max-w-5xl">
                  <div class="section-slider-inner relative lg:overflow-hidden">
                    <ClientOnly
                      fallback-tag="span"
                      fallback="Loading gallery..."
                    >
                      <Swiper
                        v-if="mutable.galleries.length"
                        :navigation="{ nextEl: '.swiper-button-next', prevEl: '.swiper-button-prev' }"
                        class="swiper-dialog overflow-visible"
                        :allow-touch-move="mutable.galleries.length > 1"
                        :auto-height="true"
                        :thumbs="{ swiper: Object.keys(mutable.thumbsSwiper).length ? mutable.thumbsSwiper : undefined }"
                        :grab-cursor="true"
                        :initial-slide="mutable.modelValue.index"
                        :slides-per-view="1"
                        :space-between="0"
                        :modules="[Navigation, Thumbs, FreeMode, Keyboard]"
                        @swiper="onSwiper"
                        @slide-change="(e) => handleSwiper(e)"
                      >
                        <SwiperSlide
                          v-for="(gallery, index) in mutable.galleries"
                          :key="index"
                        >
                          <template v-if="gallery && typeof gallery === 'object'">
                            <div class="relative mx-auto max-lg:max-w-[100vw]">
                              <figure v-if="gallery?.gallery_image" class="relative m-auto max-w-[100vw]">
                                <VueImg
                                  :src="toQueryUrl(gallery.gallery_image, props?.dimension ? {
                                    d: props.dimension,
                                  } : undefined)"
                                  :max-height="1066"
                                  :is-lazy-load="true"
                                  :alt="gallery.gallery_groups"
                                  class="h-full w-full object-cover"
                                />
                              </figure>
                            </div>
                          </template>
                        </SwiperSlide>
                      </Swiper>
                    </ClientOnly>
                  </div>
                  <div v-if="galleries[mutable.modelValue.index]?.gallery_groups" class="relative max-w-[100vw] w-full items-center max-lg:my-6 max-lg:flex max-lg:justify-between max-lg:px-2">
                    <button
                      type="button"
                      title="prev"
                      class="mr-3 text-primary lg:hidden"
                      :disabled="mutable.swipers.isBeginning"
                      @click="mutable.swipers.slidePrev()"
                    >
                      <IChevronLeft size="32" class="scale-15"/>
                    </button>
                    <div class="inline-block w-auto rounded-full bg-[var(--color-primary)] px-4 py-2 text-white capitalize lg:mt-2">
                      {{ galleries[mutable.modelValue.index].gallery_groups }}
                    </div>
                    <button
                      type="button"
                      :disabled="mutable.swipers.isEnd"
                      title="next"
                      class="ml-3 text-primary lg:hidden"
                      @click="mutable.swipers.slideNext()"
                    >
                      <IChevronRight size="32" class="scale-15"/>
                    </button>
                  </div>
                  <div class="swiper-mobile mx-auto h-full max-h-[40vh] w-full overflow-hidden bg-gray-200 px-4 py-4 lg:hidden lg:max-w-full max-lg:max-w-[100vw]">
                    <div class="swiper-mobile-inner grid-auto-rows grid grid-cols-3 gap-2 md:pb-2">
                      <figure
                        v-for="(item, index) in mutable.galleries"
                        :key="index"
                        :class="`${mutable.modelValue.index === index ? 'border-solid border-4 border-[var(--color-primary)] swiper-img-mobile-active' : ''} relative w-full swiper-img-mobile border-box`"
                        @click="slideTo(index)"
                      >
                        <VueImg
                          v-if="item.gallery_image"
                          :src="toQueryUrl(item.gallery_image, { d: '250x150' })"
                          :width="250"
                          :height="150"
                          :alt="item.gallery_groups"
                          class="h-full w-full cursor-pointer"
                        />
                      </figure>
                    </div>
                  </div>
                  <div class="mx-auto max-w-full w-full max-lg:hidden">
                    <ClientOnly
                      fallback-tag="span"
                      fallback="Loading swiper..."
                    >
                      <Swiper
                        :space-between="5"
                        slides-per-view="auto"
                        :auto-height="true"
                        :slide-to-clicked-slide="true"
                        :modules="[Thumbs, FreeMode, Keyboard]"
                        class="swiper-slide-items mt-2"
                        @swiper="setThumbsSwiper"
                      >
                        <SwiperSlide
                          v-for="(item, index) in mutable.galleries"
                          :key="index"
                          class="swiper-slide-item border-box p-0 lg:max-w-[250px] lg:min-h-[150px]"
                        >
                          <template v-if="item && item.gallery_image">
                            <figure
                              class="swiper-img-bottom relative"
                              @click="slideTo(index)"
                            >
                              <VueImg
                                :src="toQueryUrl(item.gallery_image, { d: '250x150' })"
                                :width="250"
                                :height="150"
                                :alt="item.gallery_groups"
                                class="h-full w-full cursor-pointer"
                              />
                            </figure>
                          </template>
                        </SwiperSlide>
                      </Swiper>
                    </ClientOnly>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import './gallery/component.scss';
</style>
